import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Upload, 
  Search, 
  Play, 
  Download, 
  Trash2, 
  Settings, 
  FileVideo, 
  Users, 
  Tag,
  Brain,
  Sparkles,
  Eye,
  Mic,
  Clock,
  Target,
  MessageSquare,
  Scissors,
  Database,
  CheckCircle,
  AlertCircle,
  Loader2,
  PenTool
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { 
  videoSearchApi, 
  convertBackendVideoToFrontend, 
  convertBackendSearchResultToFrontend,
  type SearchRequest,
  type ClipGenerationRequest,
  type VideoInfo
} from '@/services/videoSearchApi';
import { Video, SearchResult } from '@/types';
import ProcessingIndicator from '@/components/ProcessingIndicator';
import VideoPlayer from '@/components/VideoPlayer';
import TranscriptViewer from '@/components/TranscriptViewer';
import { VideoApi } from '@/lib/api/videoApi';
import { useAuth } from '@/contexts/AuthContext';

const Playground = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const videoApiInstance = new VideoApi();
  
  // State
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Search options
  const [searchOptions, setSearchOptions] = useState({
    searchType: 'text' as 'text' | 'visual' | 'timestamp' | 'objects' | 'speaker',
    topK: 10,
    useLLM: false,
    rerank: false,
    rerankMethod: 'openai' as 'openai' | 'cross_encoder',
    speakerFilter: '',
    objectFilter: [] as string[],
    timestamp: undefined as number | undefined,
  });

  // Indexing options
  const [indexingOptions, setIndexingOptions] = useState({
    frameInterval: 1.0,
    enableDiarization: true,
    videoId: '',
  });

  // Clip generation options
  const [clipOptions, setClipOptions] = useState({
    padding: 2.0,
    smartMerge: true,
    mergeMode: 'hybrid' as 'loose' | 'strict' | 'llm' | 'hybrid',
  });

  // Old playground state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  
  // Old playground processing options
  const [oldPlaygroundOptions, setOldPlaygroundOptions] = useState({
    enhanceQuality: true,
    detectObjects: true,
    identifySpeakers: true,
    generateChapters: false,
    resolution: 1080,
    processingIntensity: 50,
  });

  // Old playground videos query - using real API
  const { data: oldPlaygroundVideos, isLoading: isLoadingOldVideos } = useQuery({
    queryKey: ['old-videos'],
    queryFn: () => videoApiInstance.listVideos(session?.access_token || '', 1, 20),
    retry: 3,
    enabled: !!session?.access_token,
  });

  // Convert old playground videos to frontend format
  const oldVideos: Video[] = React.useMemo(() => {
    if (!oldPlaygroundVideos) return [];
    return oldPlaygroundVideos.map((backendVideo) => ({
      id: backendVideo.id,
      title: backendVideo.title,
      duration: backendVideo.duration || 0,
      thumbnail: backendVideo.thumbnail_url || '',
      processingStatus: backendVideo.status as 'pending' | 'processing' | 'completed' | 'failed',
      processingProgress: backendVideo.processing_progress,
      metadata: {
        speakers: backendVideo.speakers_detected === 'true' ? ['Speaker 1'] : [],
        objects: backendVideo.objects_detected === 'true' ? ['Object 1'] : [],
        frameCount: backendVideo.frame_count,
        segmentCount: backendVideo.segment_count,
      },
      createdAt: backendVideo.created_at,
      playbackUrl: backendVideo.playback_url,
      isRealVideo: true,
    }));
  }, [oldPlaygroundVideos]);

  // Queries
  const { data: backendVideos, isLoading: isLoadingVideos } = useQuery({
    queryKey: ['backend-videos'],
    queryFn: () => videoApiInstance.listVideos(session?.access_token || '', 1, 20),
    retry: 3,
    enabled: !!session?.access_token,
  });

  // Convert backend videos to frontend format
  const videos: Video[] = React.useMemo(() => {
    if (!backendVideos) return [];
    return backendVideos.map((backendVideo) => ({
      id: backendVideo.id,
      title: backendVideo.title,
      duration: backendVideo.duration || 0,
      thumbnail: backendVideo.thumbnail_url || '',
      processingStatus: backendVideo.status as 'pending' | 'processing' | 'completed' | 'failed',
      processingProgress: backendVideo.processing_progress,
      metadata: {
        speakers: backendVideo.speakers_detected === 'true' ? ['Speaker 1'] : [],
        objects: backendVideo.objects_detected === 'true' ? ['Object 1'] : [],
        frameCount: backendVideo.frame_count,
        segmentCount: backendVideo.segment_count,
      },
      createdAt: backendVideo.created_at,
      playbackUrl: backendVideo.playback_url,
      isRealVideo: true,
    }));
  }, [backendVideos]);

  // Mutations
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      return videoSearchApi.indexVideo(file, {
        video_id: indexingOptions.videoId || undefined,
        frame_interval: indexingOptions.frameInterval,
        enable_diarization: indexingOptions.enableDiarization,
      });
    },
    onSuccess: (data) => {
      toast({
        title: "Video indexed successfully!",
        description: `Indexed ${data.frames_count} frames and ${data.segments_count} segments.`,
      });
      queryClient.invalidateQueries({ queryKey: ['backend-videos'] });
      setUploadFile(null);
      setIndexingOptions(prev => ({ ...prev, videoId: '' }));
    },
    onError: (error) => {
      toast({
        title: "Indexing failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    }
  });

  const searchMutation = useMutation({
    mutationFn: async (request: SearchRequest) => {
      return videoSearchApi.search(request);
    },
    onSuccess: (data) => {
      const convertedResults = data.results.map(convertBackendSearchResultToFrontend);
      setSearchResults(convertedResults);
      toast({
        title: "Search completed",
        description: `Found ${data.total_results} results in ${data.search_time.toFixed(2)}s`,
      });
    },
    onError: (error) => {
      toast({
        title: "Search failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    }
  });

  const clipGenerationMutation = useMutation({
    mutationFn: async (request: ClipGenerationRequest) => {
      return videoSearchApi.generateClip(request);
    },
    onSuccess: (data) => {
      toast({
        title: "Clip generated successfully!",
        description: `Duration: ${data.duration.toFixed(1)}s`,
      });
    },
    onError: (error) => {
      toast({
        title: "Clip generation failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    }
  });

  const deleteVideoMutation = useMutation({
    mutationFn: async (videoId: string) => {
      return videoSearchApi.deleteVideo(videoId);
    },
    onSuccess: (data, videoId) => {
      toast({
        title: "Video deleted",
        description: data.message,
      });
      queryClient.invalidateQueries({ queryKey: ['backend-videos'] });
      if (selectedVideo === videoId) {
        setSelectedVideo(null);
      }
    },
    onError: (error) => {
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    }
  });

  // Handlers
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setUploadFile(file);
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a valid video file.",
        variant: "destructive"
      });
    }
  }, [toast]);

  const handleUpload = useCallback(() => {
    if (!uploadFile) return;
    
    setIsUploading(true);
    uploadMutation.mutate(uploadFile, {
      onSettled: () => setIsUploading(false)
    });
  }, [uploadFile, uploadMutation]);

  const handleSearch = useCallback(() => {
    if (!searchQuery.trim()) {
      toast({
        title: "Empty query",
        description: "Please enter a search query.",
        variant: "destructive"
      });
      return;
    }

    setIsSearching(true);
    const request: SearchRequest = {
      query: searchQuery,
      video_id: selectedVideo || undefined,
      search_type: searchOptions.searchType,
      top_k: searchOptions.topK,
      speaker_filter: searchOptions.speakerFilter || undefined,
      object_filter: searchOptions.objectFilter.length > 0 ? searchOptions.objectFilter : undefined,
      timestamp: searchOptions.timestamp,
      use_llm: searchOptions.useLLM,
      rerank: searchOptions.rerank,
      rerank_method: searchOptions.rerankMethod,
    };

    searchMutation.mutate(request, {
      onSettled: () => setIsSearching(false)
    });
  }, [searchQuery, selectedVideo, searchOptions, searchMutation, toast]);

  const handleGenerateClip = useCallback((result: SearchResult) => {
    if (!selectedVideo) return;

    const request: ClipGenerationRequest = {
      video_id: selectedVideo,
      query: searchQuery,
      start_time: result.startTime,
      end_time: result.endTime,
      padding: clipOptions.padding,
      smart_merge: clipOptions.smartMerge,
      merge_mode: clipOptions.mergeMode,
    };

    clipGenerationMutation.mutate(request);
  }, [selectedVideo, searchQuery, clipOptions, clipGenerationMutation]);

  const handleDeleteVideo = useCallback((videoId: string) => {
    deleteVideoMutation.mutate(videoId);
  }, [deleteVideoMutation]);

  // Old playground handlers
  const handleStartProcessing = useCallback(() => {
    if (!selectedVideo) {
      toast({
        title: "No video selected",
        description: "Please select a video to process.",
        variant: "destructive"
      });
      return;
    }
    
    setIsProcessing(true);
    setProcessingProgress(0);
    
    // Simulate processing progress
    const interval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsProcessing(false);
          toast({
            title: "Processing complete",
            description: "Your video has been successfully processed."
          });
          return 100;
        }
        return prev + 5;
      });
    }, 500);
    
    // In a real implementation, you would call an API here
    // videoApi.reprocessVideo(selectedVideo, oldPlaygroundOptions)
  }, [selectedVideo, oldPlaygroundOptions, toast]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Video Search Playground</h1>
        <p className="text-muted-foreground">
          Index, search, and extract clips from your video library using AI-powered analysis
        </p>
      </div>

      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="upload">Upload & Index</TabsTrigger>
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="clips">Clip Generation</TabsTrigger>
          <TabsTrigger value="library">Video Library</TabsTrigger>
          <TabsTrigger value="old-playground">Old Playground</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload & Index Video
              </CardTitle>
              <CardDescription>
                Upload a video file to index it for search. The system will extract frames, 
                transcribe speech, detect objects, and create vector embeddings.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="video-upload">Select Video File</Label>
                <Input
                  id="video-upload"
                  type="file"
                  accept="video/*"
                  onChange={handleFileUpload}
                  className="mt-2"
                />
                {uploadFile && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Selected: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="video-id">Video ID (optional)</Label>
                  <Input
                    id="video-id"
                    value={indexingOptions.videoId}
                    onChange={(e) => setIndexingOptions(prev => ({ ...prev, videoId: e.target.value }))}
                    placeholder="Auto-generated if empty"
                  />
                </div>
                <div>
                  <Label htmlFor="frame-interval">Frame Interval (seconds)</Label>
                  <Slider
                    id="frame-interval"
                    min={0.5}
                    max={5.0}
                    step={0.1}
                    value={[indexingOptions.frameInterval]}
                    onValueChange={(values) => setIndexingOptions(prev => ({ ...prev, frameInterval: values[0] }))}
                    className="mt-2"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    {indexingOptions.frameInterval}s between frames
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="diarization"
                  checked={indexingOptions.enableDiarization}
                  onCheckedChange={(checked) => setIndexingOptions(prev => ({ ...prev, enableDiarization: checked }))}
                />
                <Label htmlFor="diarization">Enable Speaker Diarization</Label>
              </div>

              <Button 
                onClick={handleUpload} 
                disabled={!uploadFile || isUploading}
                className="w-full"
              >
                {isUploading ? (
                  <>
                    <Settings className="mr-2 h-4 w-4 animate-spin" />
                    Indexing Video...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Index Video
                  </>
                )}
              </Button>

              {uploadMutation.isPending && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Processing video...</p>
                  <Progress value={uploadMutation.variables ? 50 : 0} className="w-full" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="search" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search Videos
              </CardTitle>
              <CardDescription>
                Search through indexed videos using text queries, visual content, speakers, or objects.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    placeholder="Enter your search query..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <Button onClick={handleSearch} disabled={isSearching || !searchQuery.trim()}>
                  {isSearching ? (
                    <Search className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Search Type</Label>
                  <div className="flex gap-2 mt-2">
                    {['text', 'visual', 'timestamp', 'objects', 'speaker'].map((type) => (
                      <Button
                        key={type}
                        variant={searchOptions.searchType === type ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSearchOptions(prev => ({ ...prev, searchType: type as 'text' | 'visual' | 'timestamp' | 'objects' | 'speaker' }))}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Results Count</Label>
                  <Slider
                    min={1}
                    max={50}
                    step={1}
                    value={[searchOptions.topK]}
                    onValueChange={(values) => setSearchOptions(prev => ({ ...prev, topK: values[0] }))}
                    className="mt-2"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    {searchOptions.topK} results
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="use-llm"
                    checked={searchOptions.useLLM}
                    onCheckedChange={(checked) => setSearchOptions(prev => ({ ...prev, useLLM: checked }))}
                  />
                  <Label htmlFor="use-llm">Use LLM Query Expansion</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="rerank"
                    checked={searchOptions.rerank}
                    onCheckedChange={(checked) => setSearchOptions(prev => ({ ...prev, rerank: checked }))}
                  />
                  <Label htmlFor="rerank">Rerank Results</Label>
                </div>
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold">Search Results ({searchResults.length})</h3>
                  <div className="space-y-3">
                    {searchResults.map((result, index) => (
                      <Card key={index} className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="secondary">
                                {formatTime(result.startTime || 0)}
                              </Badge>
                              {result.speaker && (
                                <Badge variant="outline">
                                  <Users className="h-3 w-3 mr-1" />
                                  {result.speaker}
                                </Badge>
                              )}
                              <Badge variant="outline">
                                {(result.relevance * 100).toFixed(0)}% match
                              </Badge>
                            </div>
                            <p className="text-sm mb-2">{result.text}</p>
                            {result.objects && result.objects.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {result.objects.map((obj, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    <Tag className="h-3 w-3 mr-1" />
                                    {obj}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleGenerateClip(result)}
                            disabled={clipGenerationMutation.isPending}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clips" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="h-5 w-5" />
                Clip Generation
              </CardTitle>
              <CardDescription>
                Generate video clips from search results with smart segment merging and AI-powered editing.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="padding">Time Padding (seconds)</Label>
                  <Slider
                    id="padding"
                    min={0}
                    max={10}
                    step={0.5}
                    value={[clipOptions.padding]}
                    onValueChange={(values) => setClipOptions(prev => ({ ...prev, padding: values[0] }))}
                    className="mt-2"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    {clipOptions.padding}s padding around segments
                  </p>
                </div>

                <div>
                  <Label>Merge Mode</Label>
                  <div className="flex gap-2 mt-2">
                    {['loose', 'strict', 'llm', 'hybrid'].map((mode) => (
                      <Button
                        key={mode}
                        variant={clipOptions.mergeMode === mode ? "default" : "outline"}
                        size="sm"
                        onClick={() => setClipOptions(prev => ({ ...prev, mergeMode: mode as 'loose' | 'strict' | 'llm' | 'hybrid' }))}
                      >
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="smart-merge"
                  checked={clipOptions.smartMerge}
                  onCheckedChange={(checked) => setClipOptions(prev => ({ ...prev, smartMerge: checked }))}
                />
                <Label htmlFor="smart-merge">Smart Segment Merging</Label>
              </div>

              {clipGenerationMutation.isPending && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Generating clip...</p>
                  <Progress value={50} className="w-full" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="library" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileVideo className="h-5 w-5" />
                Video Library
              </CardTitle>
              <CardDescription>
                Manage your indexed videos. Select a video to search within it specifically.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingVideos ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                  ))}
                </div>
              ) : videos.length === 0 ? (
                <div className="text-center py-8">
                  <FileVideo className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
                  <p className="mt-2 text-muted-foreground">No videos indexed yet</p>
                  <p className="text-sm text-muted-foreground">Upload a video to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {videos.map(video => (
                    <div 
                      key={video.id}
                      className={`p-4 rounded-md border transition-all hover:bg-accent ${
                        selectedVideo === video.id ? 'bg-accent border-primary' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-16 h-9 bg-gray-700 rounded overflow-hidden flex items-center justify-center">
                            <FileVideo className="h-6 w-6 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-sm">{video.title}</h4>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                              <Clock className="h-3 w-3" />
                              <span>{formatTime(video.duration)}</span>
                              {video.metadata?.speakers && video.metadata.speakers.length > 0 && (
                                <>
                                  <Users className="h-3 w-3" />
                                  <span>{video.metadata.speakers.length} speakers</span>
                                </>
                              )}
                              {video.metadata?.objects && video.metadata.objects.length > 0 && (
                                <>
                                  <Tag className="h-3 w-3" />
                                  <span>{video.metadata.objects.length} objects</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant={selectedVideo === video.id ? "default" : "outline"}
                            onClick={() => setSelectedVideo(selectedVideo === video.id ? null : video.id)}
                          >
                            {selectedVideo === video.id ? "Selected" : "Select"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteVideo(video.id)}
                            disabled={deleteVideoMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="old-playground" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Video Player</CardTitle>
                  <CardDescription>Select and preview videos before processing</CardDescription>
                </CardHeader>
                <CardContent>
                  {selectedVideo ? (
                    <div className="aspect-video bg-muted rounded-md flex items-center justify-center">
                      <div className="text-center">
                        <Play className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
                        <p className="mt-2 text-sm text-muted-foreground">Video preview would appear here</p>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video bg-muted rounded-md flex items-center justify-center">
                      <div className="text-center">
                        <PenTool className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
                        <p className="mt-2 text-muted-foreground">Select a video to get started</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Processing Options</CardTitle>
                  <CardDescription>Customize how your video is processed</CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="options" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="options">Basic Options</TabsTrigger>
                      <TabsTrigger value="advanced">Advanced</TabsTrigger>
                      <TabsTrigger value="output">Output Settings</TabsTrigger>
                    </TabsList>
                    <TabsContent value="options" className="space-y-4 pt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="enhanceQuality">Enhance Video Quality</Label>
                            <p className="text-sm text-muted-foreground">
                              Apply AI enhancement to improve video quality
                            </p>
                          </div>
                          <Switch
                            id="enhanceQuality"
                            checked={oldPlaygroundOptions.enhanceQuality}
                            onCheckedChange={(checked) => setOldPlaygroundOptions({...oldPlaygroundOptions, enhanceQuality: checked})}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="detectObjects">Detect Objects</Label>
                            <p className="text-sm text-muted-foreground">
                              Identify and tag objects in the video
                            </p>
                          </div>
                          <Switch
                            id="detectObjects"
                            checked={oldPlaygroundOptions.detectObjects}
                            onCheckedChange={(checked) => setOldPlaygroundOptions({...oldPlaygroundOptions, detectObjects: checked})}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="identifySpeakers">Identify Speakers</Label>
                            <p className="text-sm text-muted-foreground">
                              Detect and label different speakers
                            </p>
                          </div>
                          <Switch
                            id="identifySpeakers"
                            checked={oldPlaygroundOptions.identifySpeakers}
                            onCheckedChange={(checked) => setOldPlaygroundOptions({...oldPlaygroundOptions, identifySpeakers: checked})}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="generateChapters">Generate Chapters</Label>
                            <p className="text-sm text-muted-foreground">
                              Create automatic chapter markers
                            </p>
                          </div>
                          <Switch
                            id="generateChapters"
                            checked={oldPlaygroundOptions.generateChapters}
                            onCheckedChange={(checked) => setOldPlaygroundOptions({...oldPlaygroundOptions, generateChapters: checked})}
                          />
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="advanced" className="space-y-6 pt-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <Label htmlFor="processingIntensity">Processing Intensity</Label>
                          <span className="text-sm text-muted-foreground">{oldPlaygroundOptions.processingIntensity}%</span>
                        </div>
                        <Slider
                          id="processingIntensity"
                          min={0}
                          max={100}
                          step={10}
                          value={[oldPlaygroundOptions.processingIntensity]}
                          onValueChange={(values) => setOldPlaygroundOptions({...oldPlaygroundOptions, processingIntensity: values[0]})}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Higher intensity means more detailed processing but takes longer
                        </p>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="output" className="space-y-6 pt-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <Label htmlFor="resolution">Output Resolution</Label>
                          <span className="text-sm text-muted-foreground">{oldPlaygroundOptions.resolution}p</span>
                        </div>
                        <Slider
                          id="resolution"
                          min={480}
                          max={2160}
                          step={240}
                          value={[oldPlaygroundOptions.resolution]}
                          onValueChange={(values) => setOldPlaygroundOptions({...oldPlaygroundOptions, resolution: values[0]})}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Select the desired output resolution
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Card className="h-full flex flex-col">
                <CardHeader>
                  <CardTitle>Select Video</CardTitle>
                  <CardDescription>Choose a video to process</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-auto">
                  {isLoadingOldVideos ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {oldVideos?.map(video => (
                        <div 
                          key={video.id}
                          onClick={() => setSelectedVideo(video.id)}
                          className={`p-3 rounded-md cursor-pointer flex gap-3 transition-all hover:bg-accent ${
                            selectedVideo === video.id ? 'bg-accent' : ''
                          }`}
                        >
                          <div className="w-16 h-9 flex-shrink-0 bg-gray-700 rounded overflow-hidden">
                            {video.thumbnail && (
                              <img 
                                src={video.thumbnail} 
                                alt={video.title} 
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-sm line-clamp-1">{video.title}</h4>
                            <p className="text-xs text-muted-foreground">
                              {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                
                <div className="p-4 mt-auto border-t">
                  <Button 
                    onClick={handleStartProcessing} 
                    className="w-full" 
                    disabled={!selectedVideo || isProcessing}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    {isProcessing ? 'Processing...' : 'Start Processing'}
                  </Button>
                  
                  {isProcessing && (
                    <div className="mt-4">
                      <div className="flex justify-between mb-1 text-xs">
                        <span>Processing</span>
                        <span>{processingProgress}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${processingProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Playground;
